/**
 * Innovent Tech - Custom Theme JavaScript
 * Version: 1.0.0
 */

(function() {
  'use strict';

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const throttle = (func, limit) => {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  // ============================================
  // HEADER SCROLL BEHAVIOR
  // ============================================

  const initHeaderScroll = () => {
    const header = document.querySelector('.header, [data-header]');
    if (!header) return;

    let lastScrollY = window.scrollY;
    const scrollThreshold = 100;

    const handleScroll = throttle(() => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > scrollThreshold) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }

      lastScrollY = currentScrollY;
    }, 100);

    window.addEventListener('scroll', handleScroll, { passive: true });
  };

  // ============================================
  // MOBILE MENU
  // ============================================

  const initMobileMenu = () => {
    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    const menuClose = document.querySelector('[data-mobile-menu-close]');
    const menuBackdrop = document.querySelector('[data-mobile-menu-backdrop]');

    if (!menuToggle || !mobileMenu) return;

    const openMenu = () => {
      mobileMenu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      menuToggle.setAttribute('aria-expanded', 'true');
    };

    const closeMenu = () => {
      mobileMenu.classList.remove('is-open');
      document.body.style.overflow = '';
      menuToggle.setAttribute('aria-expanded', 'false');
    };

    menuToggle.addEventListener('click', openMenu);

    if (menuClose) {
      menuClose.addEventListener('click', closeMenu);
    }

    if (menuBackdrop) {
      menuBackdrop.addEventListener('click', closeMenu);
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  };

  // ============================================
  // SEARCH MODAL
  // ============================================

  const initSearchModal = () => {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchModal = document.querySelector('[data-search-modal]');
    const searchClose = document.querySelector('[data-search-close]');
    const searchBackdrop = document.querySelector('[data-search-backdrop]');
    const searchInput = document.querySelector('[data-search-input]');

    if (!searchToggle || !searchModal) return;

    const openSearch = () => {
      searchModal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (searchInput) {
        setTimeout(() => searchInput.focus(), 100);
      }
    };

    const closeSearch = () => {
      searchModal.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    searchToggle.addEventListener('click', openSearch);

    if (searchClose) {
      searchClose.addEventListener('click', closeSearch);
    }

    if (searchBackdrop) {
      searchBackdrop.addEventListener('click', closeSearch);
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchModal.classList.contains('is-open')) {
        closeSearch();
      }
    });
  };

  // ============================================
  // FAQ ACCORDION
  // ============================================

  const initAccordions = () => {
    const accordions = document.querySelectorAll('[data-accordion]');

    accordions.forEach(accordion => {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const content = accordion.querySelector('[data-accordion-content]');

      if (!trigger || !content) return;

      trigger.addEventListener('click', () => {
        const isOpen = accordion.classList.contains('is-open');

        // Close all other accordions in the same group
        const group = accordion.closest('[data-accordion-group]');
        if (group) {
          group.querySelectorAll('[data-accordion].is-open').forEach(item => {
            if (item !== accordion) {
              item.classList.remove('is-open');
              item.querySelector('[data-accordion-trigger]').setAttribute('aria-expanded', 'false');
            }
          });
        }

        // Toggle current accordion
        accordion.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', !isOpen);
      });
    });
  };

  // ============================================
  // PRODUCT IMAGE GALLERY
  // ============================================

  const initProductGallery = () => {
    const galleries = document.querySelectorAll('[data-product-gallery]');

    galleries.forEach(gallery => {
      const mainImage = gallery.querySelector('[data-main-image]');
      const thumbnails = gallery.querySelectorAll('[data-thumbnail]');

      if (!mainImage || thumbnails.length === 0) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const newSrc = thumb.dataset.fullImage || thumb.src;
          const newAlt = thumb.alt || '';

          // Update main image
          mainImage.src = newSrc;
          mainImage.alt = newAlt;

          // Update active state
          thumbnails.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  };

  // ============================================
  // QUANTITY SELECTOR
  // ============================================

  const initQuantitySelectors = () => {
    const selectors = document.querySelectorAll('[data-quantity-selector]');

    selectors.forEach(selector => {
      const input = selector.querySelector('[data-quantity-input]');
      const decreaseBtn = selector.querySelector('[data-quantity-decrease]');
      const increaseBtn = selector.querySelector('[data-quantity-increase]');

      if (!input) return;

      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 9999;

      const updateQuantity = (newValue) => {
        const value = Math.min(Math.max(newValue, min), max);
        input.value = value;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        // Update button states
        if (decreaseBtn) decreaseBtn.disabled = value <= min;
        if (increaseBtn) increaseBtn.disabled = value >= max;
      };

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => {
          updateQuantity(parseInt(input.value) - 1);
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => {
          updateQuantity(parseInt(input.value) + 1);
        });
      }

      input.addEventListener('change', () => {
        updateQuantity(parseInt(input.value));
      });
    });
  };

  // ============================================
  // COLLECTION FILTERS
  // ============================================

  const initCollectionFilters = () => {
    const filterToggle = document.querySelector('[data-filter-toggle]');
    const filterDrawer = document.querySelector('[data-filter-drawer]');
    const filterClose = document.querySelector('[data-filter-close]');

    if (!filterToggle || !filterDrawer) return;

    filterToggle.addEventListener('click', () => {
      filterDrawer.classList.toggle('is-open');
      document.body.classList.toggle('filter-open');
    });

    if (filterClose) {
      filterClose.addEventListener('click', () => {
        filterDrawer.classList.remove('is-open');
        document.body.classList.remove('filter-open');
      });
    }
  };

  // ============================================
  // LAZY LOADING IMAGES
  // ============================================

  const initLazyLoading = () => {
    if ('loading' in HTMLImageElement.prototype) {
      // Browser supports native lazy loading
      const images = document.querySelectorAll('img[loading="lazy"]');
      images.forEach(img => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
      });
    } else {
      // Fallback using Intersection Observer
      const lazyImages = document.querySelectorAll('img[data-src]');

      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              observer.unobserve(img);
            }
          });
        }, {
          rootMargin: '50px 0px'
        });

        lazyImages.forEach(img => imageObserver.observe(img));
      }
    }
  };

  // ============================================
  // ADD TO CART ANIMATION
  // ============================================

  const initAddToCartAnimation = () => {
    document.addEventListener('cart:item-added', (event) => {
      const cartIcon = document.querySelector('[data-cart-icon]');
      if (!cartIcon) return;

      cartIcon.classList.add('animate-bounce');
      setTimeout(() => {
        cartIcon.classList.remove('animate-bounce');
      }, 500);
    });
  };

  // ============================================
  // NEWSLETTER FORM
  // ============================================

  const initNewsletterForm = () => {
    const forms = document.querySelectorAll('[data-newsletter-form]');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        const emailInput = form.querySelector('[type="email"]');
        const message = form.querySelector('[data-newsletter-message]');

        if (!emailInput || !emailInput.value) return;

        // Show loading state
        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Subscribing...';
        }

        try {
          // Submit to Shopify's customer API
          const response = await fetch('/contact#newsletter', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              'form_type': 'customer',
              'contact[email]': emailInput.value,
              'contact[tags]': 'newsletter'
            })
          });

          if (message) {
            message.textContent = 'Thank you for subscribing!';
            message.classList.add('is-success');
          }

          emailInput.value = '';
        } catch (error) {
          if (message) {
            message.textContent = 'Something went wrong. Please try again.';
            message.classList.add('is-error');
          }
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = 'Subscribe';
          }
        }
      });
    });
  };

  // ============================================
  // SMOOTH SCROLL
  // ============================================

  const initSmoothScroll = () => {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function(e) {
        const targetId = this.getAttribute('href');
        if (targetId === '#') return;

        const target = document.querySelector(targetId);
        if (!target) return;

        e.preventDefault();

        const headerHeight = document.querySelector('.header')?.offsetHeight || 0;
        const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      });
    });
  };

  // ============================================
  // CART DRAWER (if using drawer cart)
  // ============================================

  const initCartDrawer = () => {
    const cartToggle = document.querySelector('[data-cart-toggle]');
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartClose = document.querySelector('[data-cart-close]');
    const cartBackdrop = document.querySelector('[data-cart-backdrop]');

    if (!cartDrawer) return;

    const openCart = () => {
      cartDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const closeCart = () => {
      cartDrawer.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    if (cartToggle) {
      cartToggle.addEventListener('click', (e) => {
        e.preventDefault();
        openCart();
      });
    }

    if (cartClose) {
      cartClose.addEventListener('click', closeCart);
    }

    if (cartBackdrop) {
      cartBackdrop.addEventListener('click', closeCart);
    }

    // Listen for cart updates
    document.addEventListener('cart:updated', () => {
      // Refresh cart drawer content if needed
    });
  };

  // ============================================
  // INITIALIZE ALL MODULES
  // ============================================

  const init = () => {
    initHeaderScroll();
    initMobileMenu();
    initSearchModal();
    initAccordions();
    initProductGallery();
    initQuantitySelectors();
    initCollectionFilters();
    initLazyLoading();
    initAddToCartAnimation();
    initNewsletterForm();
    initSmoothScroll();
    initCartDrawer();

    // Mark JS as loaded
    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose utility functions globally if needed
  window.InnoventTech = {
    debounce,
    throttle
  };

})();
